import React from 'react';
import {
    Box,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    VStack,
    Text,
    useColorModeValue,
    HStack,
    Flex,
    keyframes,
} from '@chakra-ui/react';

const pulseGreen = keyframes`
  0%, 100% { box-shadow: 0 0 0 2px rgba(48, 160, 130, 0.35); }
  50%       { box-shadow: 0 0 0 5px rgba(48, 160, 130, 0.06); }
`;
const pulseOrange = keyframes`
  0%, 100% { box-shadow: 0 0 0 2px rgba(237, 137, 54, 0.35); }
  50%       { box-shadow: 0 0 0 5px rgba(237, 137, 54, 0.06); }
`;
const pulseRed = keyframes`
  0%, 100% { box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.35); }
  50%       { box-shadow: 0 0 0 5px rgba(245, 101, 101, 0.06); }
`;

export interface SliderProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    label?: string;
    showValue?: boolean;
    showMarks?: boolean; // Show scale marks (0-10)
    isDisabled?: boolean;
    size?: 'sm' | 'md' | 'lg';
    w?: string | number;
    emojis?: string[] | Record<number, string>;
    showHalo?: boolean; // Show halo effect on active thumb
}

export const SimpleSlider: React.FC<SliderProps> = ({
    value,
    onChange,
    min = 0,
    max = 10,
    step = 1,
    label,
    showValue = true,
    showMarks = true,
    isDisabled = false,
    size = 'md',
    w = 'full',
    emojis,
    showHalo = true,
}) => {
    const labelColor = useColorModeValue('secondaryGray.900', 'white');
    const marksColor = useColorModeValue('gray.400', 'gray.500');
    const marksBg = useColorModeValue('gray.100', 'whiteAlpha.100');

    const sizeConfig = {
        sm: {
            sliderH: '6px',
            thumbSize: '24px',
            fontSize: 'sm',
            emojiSize: 'md',
            marksHeight: '24px',
            marksFontSize: 'xs',
            marksSpacing: '2px',
        },
        md: {
            sliderH: '8px',
            thumbSize: '32px',
            fontSize: 'md',
            emojiSize: 'lg',
            marksHeight: '32px',
            marksFontSize: 'sm',
            marksSpacing: '4px',
        },
        lg: {
            sliderH: '10px',
            thumbSize: '40px',
            fontSize: 'lg',
            emojiSize: 'xl',
            marksHeight: '40px',
            marksFontSize: 'md',
            marksSpacing: '6px',
        },
    };

    const config = sizeConfig[size];

    const getMarkColor = (markValue: number): string => {
        if (markValue <= 3) return 'green.500';
        if (markValue <= 6) return 'orange.500';
        return 'red.500';
    }

    const getMarkBg = (color: string): string => {
        const bgMap: Record<string, string> = {
            'green.500': 'rgba(48, 160, 130, 0.12)',
            'orange.500': 'rgba(237, 137, 54, 0.12)',
            'red.500': 'rgba(245, 101, 101, 0.12)',
        };
        return bgMap[color] ?? 'rgba(66, 153, 225, 0.12)';
    };

    const getHaloAnimation = (color: string) => {
        const animMap: Record<string, ReturnType<typeof keyframes>> = {
            'green.500': pulseGreen,
            'orange.500': pulseOrange,
            'red.500': pulseRed,
        };
        const anim = animMap[color] ?? pulseGreen;
        return `${anim} 5s ease-in-out infinite`;
    };

    const getCurrentEmoji = (): string | undefined => {
        if (!emojis) return undefined;
        if (Array.isArray(emojis)) {
            return emojis[value];
        } else {
            return emojis[value];
        }
    };

    const currentEmoji = getCurrentEmoji();

    // Generate marks for the scale
    const marks = Array.from({ length: max - min + 1 }, (_, i) => min + i);

    return (
        <VStack w={w} spacing={2} align="stretch">
            {(label || showValue || currentEmoji) && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <HStack spacing="8px">
                        {currentEmoji && <Text fontSize={config.emojiSize}>{currentEmoji}</Text>}
                        {label && (
                            <Text fontSize={config.fontSize} fontWeight="600" color={labelColor}>
                                {label}
                            </Text>
                        )}
                    </HStack>
                    {showValue && (
                        <Text fontSize={config.fontSize} fontWeight="700" color={getMarkColor(value)}>
                            {value}
                        </Text>
                    )}
                </Box>
            )}

            <Slider
                value={value}
                onChange={onChange}
                min={min}
                max={max}
                step={step}
                isDisabled={isDisabled}
                focusThumbOnChange={false}
            >
                <SliderTrack h={config.sliderH} bg="gray.200" borderRadius="full">
                    <SliderFilledTrack bg={getMarkColor(value)} borderRadius="full" />
                </SliderTrack>
                <SliderThumb
                    boxSize={config.thumbSize}
                    bg="white"
                    
                    
                    transition="border-color 0.1s ease"
                    sx={showHalo ? { animation: getHaloAnimation(getMarkColor(value)) } : { boxShadow: 'none' }}>
                    {currentEmoji && (
                        <Text fontSize={config.emojiSize} lineHeight="1" textAlign="center">
                            {currentEmoji}
                        </Text>
                    )}
                </SliderThumb>
            </Slider>

            {showMarks && (
                <Flex justify="space-between" align="flex-start" mt="4px" position="relative" w="full">
                    {marks.map((mark) => {
                        const markColor = getMarkColor(mark);
                        const isActive = mark === value;
                        return (
                            <VStack key={mark} spacing="3px" align="center" flex="1">
                                {/* Fixed-height container — tick grows inside, no layout shift */}
                                <Flex h="10px" align="flex-end" justify="center">
                                    <Box
                                        w={isActive ? '3px' : '2px'}
                                        h={isActive ? '10px' : '6px'}
                                        bg={markColor}
                                        borderRadius="full"
                                        opacity={isActive ? 1 : 0.4}
                                        transition="width 0.2s ease, height 0.2s ease, opacity 0.2s ease"
                                    />
                                </Flex>
                                {/* Always same padding — only bg animates, no reflow */}
                                <Text
                                    fontSize={config.marksFontSize}
                                    fontWeight={isActive ? '800' : '500'}
                                    color={isActive ? markColor : 'gray.400'}
                                    lineHeight="1"
                                    px="5px"
                                    py="2px"
                                    bg={isActive ? getMarkBg(markColor) : 'transparent'}
                                    borderRadius="md"
                                    transition="font-weight 0.15s ease, color 0.15s ease, background 0.15s ease"
                                >
                                    {mark}
                                </Text>
                            </VStack>
                        );
                    })}
                </Flex>
            )}
        </VStack>
    );
};

export default SimpleSlider;